import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { UploadDetailConfig } from '../../../feature/configuration/upload-detail-config/models/upload-config.model';
import { IMAGE_FILE_FORMATS } from '../../../feature/configuration/upload-detail-config/constants/upload-config.constants';
import { Subject } from 'rxjs';

export interface MultiFileUploadData {
  id?: string;
  file?: File;
  previewUrl?: string;
  fileName?: string;
  fileSize?: number;
  isExisting?: boolean;
  onFileChanged?: boolean;
  /** S3/storage object key, when known (needed to persist a drag-drop reorder). */
  objectName?: string;
}

@Component({
  selector: 'app-multi-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FileUploadModule,
    ButtonModule,
    TranslateModule,
    ToastModule,
    DragDropModule,
  ],
  providers: [MessageService],
  templateUrl: './multi-file-upload.component.html',
  styleUrls: ['./multi-file-upload.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiFileUploadComponent implements OnInit, OnDestroy {
  @ViewChild('fileUpload') fileUpload!: any;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Identifies which form field (controlName) this instance belongs to, so a parent form with
  // more than one multi-file field can attribute submission data to the right field.
  @Input() fieldName?: string;
  @Input() placeholder: string =
    'common.form.placeholders.click_to_upload_drag_drop';
  @Input() accept: string = '';
  @Input() maxSize: number = 5 * 1024 * 1024; // 5MB default
  @Input() acceptedTypes: string = '';
  @Input() disabled: boolean = false;
  @Input() editMode: boolean = false;
  @Input() maxFiles: number = 5;
  @Input() uploadConfig: UploadDetailConfig | null = null;
  // Enables drag-drop reordering of already-uploaded (existing) files. Off by default so other
  // forms using this component are unaffected.
  @Input() reorderable: boolean = false;
  @Input() set existingFiles(files: MultiFileUploadData[]) {
    // Clear processed file names when setting existing files
    // This ensures fresh state for new upload sessions
    this.processedFileNames.clear();

    const existingFilesArray = files || [];

    // In view mode (disabled = true), always set existing files to display them
    // In edit mode, set existing files with change detection logic
    // In add mode (not edit mode and not disabled), don't set any existing files
    if (this.disabled && !this.editMode) {
      // View mode: always set existing files to display them
      this.allImages.set(existingFilesArray);
      if (this.originalApiImages().length === 0) {
        this.originalApiImages.set(existingFilesArray);
      }
      return;
    }

    // In add mode: allow pre-filled images (e.g. from SKU selection), or clear when parent sends [] (e.g. new SKU has no images)
    if (!this.editMode && !this.disabled) {
      if (existingFilesArray.length > 0) {
        // Show pre-filled images so user can remove or replace
        this.allImages.set(existingFilesArray);
        if (this.originalApiImages().length === 0) {
          this.originalApiImages.set(existingFilesArray);
        }
      } else {
        // Parent explicitly sent empty array (e.g. new SKU has no detail images): always clear so old SKU images are removed
        this.allImages.set([]);
        this.originalApiImages.set([]);
      }
      return;
    }

    // In edit mode, only set existing files if:
    // 1. This is the initial load (originalApiImages is empty), OR
    // 2. The user hasn't made any changes (allImages matches originalApiImages)
    const currentImages = this.allImages();
    const originalImages = this.originalApiImages();

    // Compare by fileName/isExisting, not object identity — the parent re-derives this array
    // with new references on every unrelated change-detection cycle.
    const isInitialLoad = originalImages.length === 0;
    const hasUserChanges =
      currentImages.length !== originalImages.length ||
      !currentImages.every(
        (img, index) =>
          img.fileName === originalImages[index]?.fileName &&
          img.isExisting === originalImages[index]?.isExisting,
      );

    if (isInitialLoad || !hasUserChanges) {
      // Set the existing files as the initial images
      this.allImages.set(existingFilesArray);

      // CRITICAL FIX: Only store original API images ONCE when first loaded
      // Don't overwrite if originalApiImages already has data
      if (this.originalApiImages().length === 0) {
        this.originalApiImages.set(existingFilesArray);
      }
    }
    // If user has made changes, don't reset the images
  }
  get existingFiles(): MultiFileUploadData[] {
    return this.allImages().filter((f) => f.isExisting);
  }

  @Output() filesChanged = new EventEmitter<MultiFileUploadData[]>();
  @Output() filesRemoved = new EventEmitter<string[]>();
  // Fires the new order of existing (already-uploaded) files after a drag-drop reorder.
  @Output() existingImagesReordered = new EventEmitter<MultiFileUploadData[]>();

  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Reactive state - single array for all images
  allImages = signal<MultiFileUploadData[]>([]);
  originalApiImages = signal<MultiFileUploadData[]>([]); // Store original API images for comparison
  isDragOver = signal(false);
  private isProcessingFiles = false;
  private readonly processedFileNames = new Set<string>();

  // Debug method to check for duplicates in allImages signal
  private checkAllImagesDuplicates(): void {
    // Duplicate checking removed for production
  }

  // Debug method to log signal changes
  private logSignalChange(signalName: string, value: any) {
    // Signal change logging removed for production
  }

  // Computed properties
  remainingSlots = computed(() => {
    const currentFiles = this.allImages().length;
    return Math.max(0, this.maxFiles - currentFiles);
  });

  // Computed property to check if there are more than 3 files
  hasMoreFiles = computed(() => {
    return this.allImages().length > 3;
  });

  canUploadMore = computed(() => {
    return this.remainingSlots() > 0 && !this.disabled;
  });

  // Dynamic configuration based on upload config
  dynamicAccept = computed(() => {
    if (this.uploadConfig?.fileCategory === 'image') {
      const imageTypes = this.uploadConfig.fileFormat
        .map((format) => {
          switch (format.toLowerCase()) {
            case 'jpg':
            case 'jpeg':
              return 'image/jpeg';
            case 'png':
              return 'image/png';
            case 'webp':
              return 'image/webp';
            case 'gif':
              return 'image/gif';
            default:
              return `image/${format.toLowerCase()}`;
          }
        })
        .join(',');
      return imageTypes || 'image/*';
    }
    return this.accept || '*/*';
  });

  dynamicMaxSize = computed(() => {
    if (this.uploadConfig) {
      const sizeInBytes = this.uploadConfig.fileSizeLimit;
      const unit = this.uploadConfig.sizeUnit;

      // Convert to bytes based on unit
      switch (unit) {
        case 'KB':
          return sizeInBytes * 1024;
        case 'MB':
          return sizeInBytes * 1024 * 1024;
        default:
          return sizeInBytes;
      }
    }
    return this.maxSize;
  });

  dynamicPlaceholder = computed(() => {
    return this.placeholder;
  });

  ngOnInit() {
    // Try to load upload config from local storage if not provided
    if (!this.uploadConfig) {
      this.loadUploadConfigFromStorage();
    }
  }

  private loadUploadConfigFromStorage(): void {
    try {
      const storedConfigs = localStorage.getItem(
        'upload-detail-configurations'
      );
      if (storedConfigs) {
        const configs = JSON.parse(storedConfigs);
        // Find config for product asset add
        const productAssetConfig = configs.find(
          (config: any) =>
            config.group === 'product-asset-add' &&
            config.imageType === 'Detail Images'
        );
        if (productAssetConfig) {
          this.uploadConfig = productAssetConfig;
          // Trigger change detection to update the UI
          this.cdr.detectChanges();
        }
      }
    } catch {
      // Silently handle error loading upload config from storage
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelect(event: any) {
    const files: File[] = event.files || [];

    if (files.length === 0) return;

    // Validate file count
    const currentCount = this.allImages().length;
    if (currentCount + files.length > this.maxFiles) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.validation.max_files_exceeded'
        ),
        detail: this.translateService.instant(
          'common.validation.max_files_detail',
          { max: this.maxFiles }
        ),
      });
      return;
    }

    // Validate each file
    const validFiles: MultiFileUploadData[] = [];

    for (const file of files) {
      if (this.validateFile(file)) {
        const fileData: MultiFileUploadData = {
          id: this.generateFileId(),
          file: file,
          fileName: file.name,
          fileSize: file.size,
          previewUrl: this.createPreviewUrl(file),
          isExisting: false,
          onFileChanged: true,
        };
        validFiles.push(fileData);
      }
    }

    if (validFiles.length > 0) {
      const currentFiles = this.allImages();
      const newUploadedFiles = [...currentFiles, ...validFiles];
      this.logSignalChange('allImages', newUploadedFiles);
      this.allImages.set(newUploadedFiles);
      this.emitFilesChanged();
    }
  }

  onFileRemove(event: any) {
    const fileToRemove = event.file;
    const currentFiles = this.allImages();

    // Find and remove the file
    const updatedFiles = currentFiles.filter((file) => {
      if (file.file === fileToRemove) {
        // Emit removed file ID for parent component to track
        if (file.id) {
          this.filesRemoved.emit([file.id]);
        }
        return false;
      }
      return true;
    });

    this.allImages.set(updatedFiles);
    this.emitFilesChanged();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    // Only allow drag over if we can upload more files
    if (this.disabled || !this.canUploadMore()) {
      return;
    }

    // Check if the dragged items contain files
    if (event.dataTransfer?.types.includes('Files')) {
      this.isDragOver.set(true);
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    // Only reset drag state if we're leaving the drop zone entirely
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      this.isDragOver.set(false);
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    if (this.disabled || !this.canUploadMore()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.validation.upload_disabled'
        ),
        detail: this.translateService.instant(
          'common.validation.max_files_reached_or_disabled'
        ),
      });
      return;
    }

    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length > 0) {
      // Validate file count before processing
      const currentCount = this.allImages().length;
      if (currentCount + files.length > this.maxFiles) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant(
            'common.validation.max_files_exceeded'
          ),
          detail: this.translateService.instant(
            'common.validation.max_files_detail',
            { max: this.maxFiles }
          ),
        });
        return;
      }

      // Process files directly
      this.processFiles(files, 'dragdrop');
    }
  }

  removeFile(fileToRemove: MultiFileUploadData) {
    // Simply remove the file from allImages
    const currentImages = this.allImages();
    const updatedImages = currentImages.filter((file) => file !== fileToRemove);
    this.allImages.set(updatedImages);

    // Clear the processedFileNames entry for this file to allow re-upload
    const fileKey = `${fileToRemove.fileName}-${fileToRemove.fileSize}`;
    this.processedFileNames.delete(fileKey);

    // Emit removed file ID for parent component to track
    if (fileToRemove.id) {
      this.filesRemoved.emit([fileToRemove.id]);
    }

    this.emitFilesChanged();
  }

  onReorderDrop(event: CdkDragDrop<MultiFileUploadData[]>): void {
    if (!this.reorderable || event.previousIndex === event.currentIndex) {
      return;
    }

    const reordered = [...this.allImages()];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.allImages.set(reordered);

    this.existingImagesReordered.emit(
      reordered.filter((f) => f.isExisting),
    );
  }

  private validateFile(file: File): boolean {
    // Check file size
    const maxSize = this.dynamicMaxSize();
    if (file.size > maxSize) {
      const maxSizeMB = Math.round((maxSize / (1024 * 1024)) * 100) / 100;
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'common.validation.file_too_large'
        ),
        detail: this.translateService.instant(
          'common.validation.file_size_limit',
          {
            maxSize: maxSizeMB,
            unit: 'MB',
          }
        ),
      });
      return false;
    }

    // Check file type by extension — MIME type is unreliable (e.g. HEIC/PDF report an
    // empty or non-image type in some browsers) so it can't be trusted alone.
    const allowedFormats = this.getAllowedFileFormats();
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (!allowedFormats.includes(extension)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'common.validation.invalid_file_type'
        ),
        detail: this.translateService.instant(
          'common.validation.accepted_types',
          {
            types: allowedFormats.join(', ').toUpperCase(),
          }
        ),
      });
      return false;
    }

    return true;
  }

  private getAllowedFileFormats(): string[] {
    if (
      this.uploadConfig?.fileCategory === 'image' &&
      this.uploadConfig.fileFormat?.length
    ) {
      return this.uploadConfig.fileFormat.map((format) =>
        format.toLowerCase()
      );
    }
    return IMAGE_FILE_FORMATS.map((format) => format.value);
  }

  private createPreviewUrl(file: File): string {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return '';
  }

  private generateFileId(): string {
    // Generate a proper UUID for new uploaded files
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  private emitFilesChanged() {
    const allImages = this.allImages();
    this.filesChanged.emit(allImages);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileStatusText(file: MultiFileUploadData): string {
    // Check if it's a new uploaded file (blob URL)
    if (file.previewUrl?.startsWith('blob:')) {
      return this.translateService.instant('material.form.file_status.new');
    }

    return this.translateService.instant('material.form.file_status.existing');
  }

  getFileStatusClass(file: MultiFileUploadData): string {
    // Check if it's a new uploaded file (blob URL)
    if (file.previewUrl?.startsWith('blob:')) {
      return 'new';
    }

    return 'existing';
  }

  getAcceptedFormatsText(): string {
    // First try to get from upload config
    if (
      this.uploadConfig?.fileFormat &&
      Array.isArray(this.uploadConfig.fileFormat)
    ) {
      const formats = this.uploadConfig.fileFormat.join(', ').toUpperCase();
      return formats;
    }

    // Fallback to acceptedTypes input (but skip if it's a translation key)
    if (
      this.acceptedTypes &&
      this.acceptedTypes !==
        'common.form.placeholders.accepted_material_image_types' &&
      !this.acceptedTypes.startsWith('common.form.placeholders.')
    ) {
      return this.acceptedTypes;
    }

    // Default fallback
    return 'JPG, JPEG, PNG, GIF, WEBP';
  }

  onChooseFiles(): void {
    if (this.fileInput?.nativeElement && !this.disabled) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (files && files.length > 0) {
      const fileArray = Array.from(files);

      // Check if we're already processing files to prevent double processing
      if (this.isProcessingFiles) {
        target.value = '';
        return;
      }

      this.isProcessingFiles = true;

      // Reset the input value immediately to prevent multiple triggers
      target.value = '';

      // Process files directly instead of calling onFileSelect
      this.processFiles(fileArray, 'choose');

      // Reset processing flag after a short delay
      setTimeout(() => {
        this.isProcessingFiles = false;
      }, 200);
    }
  }

  private processFiles(
    files: File[],
    uploadMethod: 'choose' | 'dragdrop' = 'choose'
  ): void {
    this.logProcessFilesStart(files);

    if (files.length === 0) return;

    // Clear processed file names at the start of each upload session
    // This allows re-uploading files with the same name/size
    this.processedFileNames.clear();

    if (!this.validateFileCount(files.length)) return;

    const validFiles = this.processValidFiles(files);
    this.addValidFilesToUploaded(validFiles, uploadMethod);
  }

  private logProcessFilesStart(files: File[]): void {
    // Process files start logging removed for production
  }

  private validateFileCount(newFilesCount: number): boolean {
    const currentCount = this.allImages().length;

    if (currentCount + newFilesCount > this.maxFiles) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.validation.max_files_exceeded'
        ),
        detail: this.translateService.instant(
          'common.validation.max_files_detail',
          { max: this.maxFiles }
        ),
      });
      return false;
    }
    return true;
  }

  private processValidFiles(files: File[]): MultiFileUploadData[] {
    const validFiles: MultiFileUploadData[] = [];
    const currentImages = this.allImages();

    for (const file of files) {
      const fileData = this.processSingleFile(file, currentImages);
      if (fileData) {
        validFiles.push(fileData);
      }
    }

    return validFiles;
  }

  private processSingleFile(
    file: File,
    currentImages: MultiFileUploadData[]
  ): MultiFileUploadData | null {
    const fileKey = `${file.name}-${file.size}`;

    if (!this.validateFile(file)) {
      return null;
    }

    const isDuplicate = this.isFileDuplicate(file, currentImages);
    const isAlreadyProcessed = this.processedFileNames.has(fileKey);

    if (isDuplicate || isAlreadyProcessed) {
      return null;
    }

    const fileData = this.createFileData(file);
    this.processedFileNames.add(fileKey);

    return fileData;
  }

  private isFileDuplicate(
    file: File,
    currentImages: MultiFileUploadData[]
  ): boolean {
    // Only check for duplicates against existing API files, not newly uploaded files
    return currentImages.some(
      (existingFile) =>
        existingFile.isExisting === true &&
        existingFile.fileName === file.name &&
        existingFile.fileSize === file.size
    );
  }

  private createFileData(file: File): MultiFileUploadData {
    const fileData: MultiFileUploadData = {
      id: this.generateFileId(),
      file: file,
      fileName: file.name,
      fileSize: file.size,
      previewUrl: this.createPreviewUrl(file),
      isExisting: false,
      onFileChanged: true,
    };

    // Ensure newly uploaded files NEVER get existing- IDs
    if (fileData.id?.startsWith('existing-')) {
      fileData.id = this.generateFileId();
    }

    return fileData;
  }

  private addValidFilesToUploaded(
    validFiles: MultiFileUploadData[],
    uploadMethod: 'choose' | 'dragdrop'
  ): void {
    if (validFiles.length === 0) {
      return;
    }

    const currentImages = this.allImages();

    // Filter out any files that already exist to prevent duplicates
    // Only check against existing API files, not newly uploaded files
    const filteredValidFiles = validFiles.filter((newFile) => {
      const isDuplicate = currentImages.some(
        (existingFile) =>
          existingFile.isExisting === true &&
          existingFile.fileName === newFile.fileName &&
          existingFile.fileSize === newFile.fileSize
      );

      return !isDuplicate;
    });

    // Simply push new files to the allImages array
    const newAllImages = [...currentImages, ...filteredValidFiles];

    this.logSignalChange('allImages', newAllImages);
    this.allImages.set(newAllImages);

    this.emitFilesChanged();
    this.showSuccessMessage(uploadMethod, filteredValidFiles.length);
  }

  private showSuccessMessage(
    uploadMethod: 'choose' | 'dragdrop',
    fileCount: number
  ): void {
    const summaryKey =
      uploadMethod === 'dragdrop'
        ? 'common.success.files_dropped'
        : 'common.success.files_selected';

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant(summaryKey),
      detail: this.translateService.instant('common.success.files_processed', {
        count: fileCount,
      }),
    });
  }

  onUploadFiles(): void {
    // Emit all images for upload
    this.filesChanged.emit(this.allImages());
  }

  /**
   * Debug method to manually check current state
   * Call this from browser console: component.getCurrentStateSummary()
   */
  debugCurrentState(): void {
    this.getCurrentStateSummary();
  }

  /**
   * Debug method to check if getSubmissionData is working correctly
   * Call this from browser console: component.debugSubmissionData()
   */
  debugSubmissionData(): {
    detailImages: File[];
    removeDetailImages: string[];
  } {
    const submissionData = this.getSubmissionData();
    return submissionData;
  }

  /**
   * Debug method to clear processed file names
   * Call this from browser console: component.clearProcessedFiles()
   */
  clearProcessedFiles(): void {
    this.processedFileNames.clear();
  }

  /**
   * Debug method to manually trigger cancel upload
   * Call this from browser console: component.debugCancelUpload()
   */
  debugCancelUpload(): void {
    this.onCancelUpload();
  }

  /**
   * Generate submission data by comparing original API images with current state
   * @returns Object containing detailImages (new uploads) and removeDetailImages (removed files)
   */
  getSubmissionData(): {
    detailImages: File[];
    removeDetailImages: string[];
  } {
    const originalImages = this.originalApiImages();
    const currentImages = this.allImages();

    const detailImages: File[] = [];
    const removeDetailImages: string[] = [];

    // 1. Check for removed images: items in original but not in current
    originalImages.forEach((originalImage) => {
      // Only existing API images can be removed on backend.
      // New client-side uploads must never be sent as removeDetailImages.
      if (!originalImage.isExisting) {
        return;
      }

      const stillExists = currentImages.some(
        (currentImage) =>
          currentImage.fileName === originalImage.fileName &&
          currentImage.isExisting === true
      );

      if (!stillExists) {
        // This image was removed
        if (originalImage.fileName) {
          removeDetailImages.push(originalImage.fileName);
        }
      }
    });

    // 2. Check for new uploads: items in current but not in original
    currentImages.forEach((currentImage) => {
      if (!currentImage.isExisting) {
        // This is a newly uploaded image
        if (currentImage.file) {
          detailImages.push(currentImage.file);
        }
      }
    });

    return {
      detailImages,
      removeDetailImages,
    };
  }

  /**
   * Reset the original API images (useful when switching modes or clearing data)
   */
  resetOriginalImages(): void {
    this.originalApiImages.set([]);
  }

  /**
   * Get current state summary for debugging
   */
  getCurrentStateSummary(): void {
    // State summary logging removed for production
  }

  onCancelUpload(): void {
    if (this.editMode) {
      // In edit mode: restore to original API images only
      this.allImages.set([...this.originalApiImages()]);
    } else {
      // In add mode: clear all files
      this.allImages.set([]);
    }

    // Clear processed file names to allow re-upload
    this.processedFileNames.clear();

    this.emitFilesChanged();
  }
}
